// services/territorial-api.service.ts
import {
  SearchResultsDto,
  QueryOptionsDto,
  PaginationResponseDto,
  CETerritory,
  Circle,
  BulkOperationResultDto,
  Division,
  SubDivision,
  HierarchyResponseDto,
  TerritorialStatsDto,
  District,
  Mandal
} from "../types/territorial.types";

/**
 * Comprehensive Territorial API Service
 * Handles all territorial hierarchy operations with proper error handling,
 * caching, and performance optimizations
 */
class TerritorialAPIService {
  private baseURL: string;
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds
  private retryAttempts = 3;
  private retryDelay = 1000;

  constructor(baseURL?: string) {
    this.baseURL = baseURL || process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  }

  /**
   * Generic HTTP request method with retry logic and error handling
   */
  private async request<T>(
    endpoint: string, 
    options?: RequestInit,
    useCache: boolean = true,
    cacheTTL: number = this.DEFAULT_TTL
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const cacheKey = `${options?.method || 'GET'}_${url}_${JSON.stringify(options?.body || {})}`;

    // Check cache for GET requests
    if (useCache && (!options?.method || options.method === 'GET')) {
      const cached = this.getFromCache<T>(cacheKey);
      if (cached) return cached;
    }

    let lastError: Error;
    
    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...options?.headers,
          },
          signal: controller.signal,
          ...options,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || 
            `HTTP ${response.status}: ${response.statusText}`
          );
        }

        const data = await response.json();

        // Cache successful GET requests
        if (useCache && (!options?.method || options.method === 'GET')) {
          this.setCache(cacheKey, data, cacheTTL);
        }

        return data;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        // Don't retry on client errors (4xx) or last attempt
        if (attempt === this.retryAttempts || (error as any)?.status >= 400 && (error as any)?.status < 500) {
          break;
        }
        
        // Wait before retry with exponential backoff
        await this.delay(this.retryDelay * Math.pow(2, attempt - 1));
      }
    }

    throw lastError!;
  }

  /**
   * Cache management methods
   */
  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  private setCache(key: string, data: any, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Build query parameters from QueryOptionsDto
   */
  private buildQueryParams(query?: QueryOptionsDto): string {
    if (!query) return '';
    
    const params = new URLSearchParams();
    
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, String(v)));
        } else if (value instanceof Date) {
          params.append(key, value.toISOString().split('T')[0]);
        } else {
          params.append(key, String(value));
        }
      }
    });
    
    return params.toString();
  }

  // ==========================================
  // CE TERRITORIES API
  // ==========================================

  /**
   * Get all CE Territories with pagination and filtering
   */
  getCETerritories = async (query?: QueryOptionsDto): Promise<PaginationResponseDto<CETerritory>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/ce-territories${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<CETerritory>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch CE territories: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get CE Territory by ID with optional relations
   */
  getCETerritoryById = async (id: number, includeRelations?: boolean): Promise<CETerritory> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<CETerritory>(`/ce-territories/${id}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch CE territory ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get CE Territory by code
   */
  getCETerritoryByCode = async (code: string, includeRelations?: boolean): Promise<CETerritory> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<CETerritory>(`/ce-territories/code/${encodeURIComponent(code)}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch CE territory with code ${code}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Create new CE Territory
   */
  createCETerritory = async (data: Partial<CETerritory>): Promise<CETerritory> => {
    try {
      return await this.request<CETerritory>('/ce-territories', {
        method: 'POST',
        body: JSON.stringify(data),
      }, false);
    } catch (error) {
      throw new Error(`Failed to create CE territory: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Update CE Territory
   */
  updateCETerritory = async (id: number, data: Partial<CETerritory>): Promise<CETerritory> => {
    try {
      const result = await this.request<CETerritory>(`/ce-territories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }, false);
      
      // Invalidate related cache entries
      this.invalidateCache(`/ce-territories`);
      
      return result;
    } catch (error) {
      throw new Error(`Failed to update CE territory ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Delete CE Territory
   */
  deleteCETerritory = async (id: number): Promise<void> => {
    try {
      await this.request<void>(`/ce-territories/${id}`, {
        method: 'DELETE',
      }, false);
      
      // Invalidate cache
      this.invalidateCache(`/ce-territories`);
    } catch (error) {
      throw new Error(`Failed to delete CE territory ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Soft delete CE Territory
   */
  softDeleteCETerritory = async (id: number): Promise<CETerritory> => {
    try {
      const result = await this.request<CETerritory>(`/ce-territories/${id}/soft-delete`, {
        method: 'PUT',
      }, false);
      
      this.invalidateCache(`/ce-territories`);
      return result;
    } catch (error) {
      throw new Error(`Failed to soft delete CE territory ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get CE Territory count with filters
   */
  getCETerritoryCount = async (filters?: any): Promise<{ count: number }> => {
    try {
      const queryString = filters ? `?${new URLSearchParams(filters)}` : '';
      return await this.request<{ count: number }>(`/ce-territories/count${queryString}`);
    } catch (error) {
      throw new Error(`Failed to get CE territory count: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Check if CE Territory exists
   */
  checkCETerritoryExists = async (id: number): Promise<{ exists: boolean }> => {
    try {
      return await this.request<{ exists: boolean }>(`/ce-territories/exists/${id}`);
    } catch (error) {
      throw new Error(`Failed to check CE territory existence: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // CIRCLES API
  // ==========================================

  /**
   * Get all Circles with pagination and filtering
   */
  getCircles = async (query?: QueryOptionsDto): Promise<PaginationResponseDto<Circle>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/circles${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<Circle>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch circles: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Circle by ID
   */
  getCircleById = async (id: number, includeRelations?: boolean): Promise<Circle> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<Circle>(`/circles/${id}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch circle ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Circle by code
   */
  getCircleByCode = async (code: string, includeRelations?: boolean): Promise<Circle> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<Circle>(`/circles/code/${encodeURIComponent(code)}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch circle with code ${code}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Circles by CE Territory
   */
  getCirclesByCETerritory = async (ceTerritoryId: number, query?: QueryOptionsDto): Promise<PaginationResponseDto<Circle>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/circles/ce-territory/${ceTerritoryId}${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<Circle>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch circles for CE territory ${ceTerritoryId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Circle statistics
   */
  getCircleStatistics = async (): Promise<any> => {
    try {
      return await this.request<any>('/circles/statistics');
    } catch (error) {
      throw new Error(`Failed to fetch circle statistics: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get active Circles
   */
  getActiveCircles = async (): Promise<Circle[]> => {
    try {
      return await this.request<Circle[]>('/circles/active');
    } catch (error) {
      throw new Error(`Failed to fetch active circles: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Create Circle
   */
  createCircle = async (data: Partial<Circle>): Promise<Circle> => {
    try {
      return await this.request<Circle>('/circles', {
        method: 'POST',
        body: JSON.stringify(data),
      }, false);
    } catch (error) {
      throw new Error(`Failed to create circle: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Bulk create Circles
   */
  bulkCreateCircles = async (circles: Partial<Circle>[]): Promise<BulkOperationResultDto> => {
    try {
      return await this.request<BulkOperationResultDto>('/circles/bulk', {
        method: 'POST',
        body: JSON.stringify({ circles }),
      }, false);
    } catch (error) {
      throw new Error(`Failed to bulk create circles: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Update Circle
   */
  updateCircle = async (id: number, data: Partial<Circle>): Promise<Circle> => {
    try {
      const result = await this.request<Circle>(`/circles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }, false);
      
      this.invalidateCache(`/circles`);
      return result;
    } catch (error) {
      throw new Error(`Failed to update circle ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Delete Circle
   */
  deleteCircle = async (id: number): Promise<void> => {
    try {
      await this.request<void>(`/circles/${id}`, {
        method: 'DELETE',
      }, false);
      
      this.invalidateCache(`/circles`);
    } catch (error) {
      throw new Error(`Failed to delete circle ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // DISTRICTS API
  // ==========================================

  /**
   * Get all Districts
   */
  getDistricts = async (query?: QueryOptionsDto): Promise<PaginationResponseDto<District>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/districts${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<District>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch districts: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get District by ID
   */
  getDistrictById = async (id: number, includeRelations?: boolean): Promise<District> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<District>(`/districts/${id}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch district ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Districts by Circle
   */
  getDistrictsByCircle = async (circleId: number, query?: QueryOptionsDto): Promise<PaginationResponseDto<District>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/districts/circle/${circleId}${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<District>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch districts for circle ${circleId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Create District
   */
  createDistrict = async (data: Partial<District>): Promise<District> => {
    try {
      return await this.request<District>('/districts', {
        method: 'POST',
        body: JSON.stringify(data),
      }, false);
    } catch (error) {
      throw new Error(`Failed to create district: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Update District
   */
  updateDistrict = async (id: number, data: Partial<District>): Promise<District> => {
    try {
      const result = await this.request<District>(`/districts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }, false);
      
      this.invalidateCache(`/districts`);
      return result;
    } catch (error) {
      throw new Error(`Failed to update district ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Delete District
   */
  deleteDistrict = async (id: number): Promise<void> => {
    try {
      await this.request<void>(`/districts/${id}`, {
        method: 'DELETE',
      }, false);
      
      this.invalidateCache(`/districts`);
    } catch (error) {
      throw new Error(`Failed to delete district ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // DIVISIONS API
  // ==========================================

  /**
   * Get all Divisions
   */
  getDivisions = async (query?: QueryOptionsDto): Promise<PaginationResponseDto<Division>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/divisions${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<Division>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch divisions: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Division by ID
   */
  getDivisionById = async (id: number, includeRelations?: boolean): Promise<Division> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<Division>(`/divisions/${id}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch division ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Divisions by District
   */
  getDivisionsByDistrict = async (districtId: number, query?: QueryOptionsDto): Promise<PaginationResponseDto<Division>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/divisions/district/${districtId}${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<Division>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch divisions for district ${districtId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Create Division
   */
  createDivision = async (data: Partial<Division>): Promise<Division> => {
    try {
      return await this.request<Division>('/divisions', {
        method: 'POST',
        body: JSON.stringify(data),
      }, false);
    } catch (error) {
      throw new Error(`Failed to create division: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Update Division
   */
  updateDivision = async (id: number, data: Partial<Division>): Promise<Division> => {
    try {
      const result = await this.request<Division>(`/divisions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }, false);
      
      this.invalidateCache(`/divisions`);
      return result;
    } catch (error) {
      throw new Error(`Failed to update division ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Delete Division
   */
  deleteDivision = async (id: number): Promise<void> => {
    try {
      await this.request<void>(`/divisions/${id}`, {
        method: 'DELETE',
      }, false);
      
      this.invalidateCache(`/divisions`);
    } catch (error) {
      throw new Error(`Failed to delete division ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // SUB-DIVISIONS API
  // ==========================================

  /**
   * Get all Sub-Divisions
   */
  getSubDivisions = async (query?: QueryOptionsDto): Promise<PaginationResponseDto<SubDivision>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/sub-divisions${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<SubDivision>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch sub-divisions: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Sub-Division by ID
   */
  getSubDivisionById = async (id: number, includeRelations?: boolean): Promise<SubDivision> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<SubDivision>(`/sub-divisions/${id}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch sub-division ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Sub-Divisions by Division
   */
  getSubDivisionsByDivision = async (divisionId: number, query?: QueryOptionsDto): Promise<PaginationResponseDto<SubDivision>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/sub-divisions/division/${divisionId}${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<SubDivision>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch sub-divisions for division ${divisionId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Create Sub-Division
   */
  createSubDivision = async (data: Partial<SubDivision>): Promise<SubDivision> => {
    try {
      return await this.request<SubDivision>('/sub-divisions', {
        method: 'POST',
        body: JSON.stringify(data),
      }, false);
    } catch (error) {
      throw new Error(`Failed to create sub-division: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Update Sub-Division
   */
  updateSubDivision = async (id: number, data: Partial<SubDivision>): Promise<SubDivision> => {
    try {
      const result = await this.request<SubDivision>(`/sub-divisions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }, false);
      
      this.invalidateCache(`/sub-divisions`);
      return result;
    } catch (error) {
      throw new Error(`Failed to update sub-division ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Delete Sub-Division
   */
  deleteSubDivision = async (id: number): Promise<void> => {
    try {
      await this.request<void>(`/sub-divisions/${id}`, {
        method: 'DELETE',
      }, false);
      
      this.invalidateCache(`/sub-divisions`);
    } catch (error) {
      throw new Error(`Failed to delete sub-division ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // MANDALS API
  // ==========================================

  /**
   * Get all Mandals
   */
  getMandals = async (query?: QueryOptionsDto): Promise<PaginationResponseDto<Mandal>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/mandals${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<Mandal>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch mandals: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Mandal by ID
   */
  getMandalById = async (id: number, includeRelations?: boolean): Promise<Mandal> => {
    try {
      const params = includeRelations ? '?includeRelations=true' : '';
      return await this.request<Mandal>(`/mandals/${id}${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch mandal ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Mandals by Sub-Division
   */
  getMandalsBySubDivision = async (subDivisionId: number, query?: QueryOptionsDto): Promise<PaginationResponseDto<Mandal>> => {
    try {
      const queryString = this.buildQueryParams(query);
      const endpoint = `/mandals/sub-division/${subDivisionId}${queryString ? `?${queryString}` : ''}`;
      
      return await this.request<PaginationResponseDto<Mandal>>(endpoint);
    } catch (error) {
      throw new Error(`Failed to fetch mandals for sub-division ${subDivisionId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Mandal hierarchy
   */
  getMandalHierarchy = async (id: number): Promise<HierarchyResponseDto> => {
    try {
      return await this.request<HierarchyResponseDto>(`/mandals/${id}/hierarchy`);
    } catch (error) {
      throw new Error(`Failed to fetch mandal hierarchy for ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get largest Mandals by area
   */
  getLargestMandalsByArea = async (limit?: number): Promise<Mandal[]> => {
    try {
      const params = limit ? `?limit=${limit}` : '';
      return await this.request<Mandal[]>(`/mandals/largest/area${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch largest mandals by area: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get largest Mandals by population
   */
  getLargestMandalsByPopulation = async (limit?: number): Promise<Mandal[]> => {
    try {
      const params = limit ? `?limit=${limit}` : '';
      return await this.request<Mandal[]>(`/mandals/largest/population${params}`);
    } catch (error) {
      throw new Error(`Failed to fetch largest mandals by population: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Search Mandals by location
   */
  searchMandalsByLocation = async (lat: number, lng: number, radius?: number): Promise<Mandal[]> => {
    try {
      const params = new URLSearchParams({
        lat: String(lat),
        lng: String(lng),
        ...(radius && { radius: String(radius) }),
      });
      return await this.request<Mandal[]>(`/mandals/search/location?${params}`);
    } catch (error) {
      throw new Error(`Failed to search mandals by location: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Create Mandal
   */
  createMandal = async (data: Partial<Mandal>): Promise<Mandal> => {
    try {
      return await this.request<Mandal>('/mandals', {
        method: 'POST',
        body: JSON.stringify(data),
      }, false);
    } catch (error) {
      throw new Error(`Failed to create mandal: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Update Mandal
   */
  updateMandal = async (id: number, data: Partial<Mandal>): Promise<Mandal> => {
    try {
      const result = await this.request<Mandal>(`/mandals/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }, false);
      
      this.invalidateCache(`/mandals`);
      return result;
    } catch (error) {
      throw new Error(`Failed to update mandal ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Delete Mandal
   */
  deleteMandal = async (id: number): Promise<void> => {
    try {
      await this.request<void>(`/mandals/${id}`, {
        method: 'DELETE',
      }, false);
      
      this.invalidateCache(`/mandals`);
    } catch (error) {
      throw new Error(`Failed to delete mandal ${id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // GLOBAL/AGGREGATE API METHODS
  // ==========================================

  /**
   * Get territorial statistics
   */
  getTerritorialStats = async (): Promise<TerritorialStatsDto> => {
    try {
      return await this.request<TerritorialStatsDto>('/territorial/statistics');
    } catch (error) {
      throw new Error(`Failed to fetch territorial statistics: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Search across all territorial entities
   */
  searchTerritorial = async (term: string, filters?: Record<string, any>): Promise<SearchResultsDto> => {
    try {
      const params = new URLSearchParams({ term });
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
      }
      return await this.request<SearchResultsDto>(`/territorial/search?${params}`);
    } catch (error) {
      throw new Error(`Failed to search territorial data: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Export territorial data to Excel/CSV
   */
  exportTerritorialData = async (format: 'excel' | 'csv' = 'excel'): Promise<Blob> => {
    try {
      const response = await fetch(`${this.baseURL}/territorial/export?format=${format}`, {
        method: 'GET',
        headers: {
          'Accept': format === 'excel' 
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }

      return response.blob();
    } catch (error) {
      throw new Error(`Failed to export territorial data: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Seed territorial data (for development/testing)
   */
  seedTerritorialData = async (): Promise<{ message: string }> => {
    try {
      return await this.request<{ message: string }>('/territorial/seed', {
        method: 'POST',
      }, false);
    } catch (error) {
      throw new Error(`Failed to seed territorial data: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // ENHANCED METHODS WITH STATISTICS
  // ==========================================

  /**
   * Get Circles with statistics
   */
  getCirclesWithStats = async (): Promise<Circle[]> => {
    try {
      return await this.request<Circle[]>('/circles/with-stats');
    } catch (error) {
      throw new Error(`Failed to fetch circles with statistics: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Divisions with statistics
   */
  getDivisionsWithStats = async (): Promise<Division[]> => {
    try {
      return await this.request<Division[]>('/divisions/with-stats');
    } catch (error) {
      throw new Error(`Failed to fetch divisions with statistics: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Sub-Divisions with statistics
   */
  getSubDivisionsWithStats = async (): Promise<SubDivision[]> => {
    try {
      return await this.request<SubDivision[]>('/sub-divisions/with-stats');
    } catch (error) {
      throw new Error(`Failed to fetch sub-divisions with statistics: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get Divisions by Circle
   */
  getDivisionsByCircle = async (circleId: number): Promise<Division[]> => {
    try {
      return await this.request<Division[]>(`/divisions/circle/${circleId}`);
    } catch (error) {
      throw new Error(`Failed to fetch divisions for circle ${circleId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // CACHE MANAGEMENT METHODS
  // ==========================================

  /**
   * Clear all cache
   */
  clearCache = (): void => {
    this.cache.clear();
  };

  /**
   * Clear cache by pattern
   */
  clearCacheByPattern = (pattern: string): void => {
    const keysToDelete: string[] = [];
    this.cache.forEach((value, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  };

  /**
   * Invalidate cache entries
   */
  private invalidateCache = (pattern: string): void => {
    this.clearCacheByPattern(pattern);
  };

  /**
   * Get cache statistics
   */
  getCacheStats = (): {
    totalEntries: number;
    cacheSize: number;
    hitRate: number;
  } => {
    const totalEntries = this.cache.size;
    
    // Convert cache to array for size calculation (compatible with older TS targets)
    const cacheEntries: Array<[string, { data: any; timestamp: number; ttl: number }]> = [];
    this.cache.forEach((value, key) => {
      cacheEntries.push([key, value]);
    });
    const cacheSize = JSON.stringify(cacheEntries).length;
    
    return {
      totalEntries,
      cacheSize,
      hitRate: 0 // You can implement hit rate tracking if needed
    };
  };

  // ==========================================
  // BATCH OPERATIONS
  // ==========================================

  /**
   * Batch get multiple entities by IDs
   */
  batchGetEntities = async <T>(
    entityType: 'territories' | 'circles' | 'districts' | 'divisions' | 'sub-divisions' | 'mandals',
    ids: number[]
  ): Promise<T[]> => {
    try {
      const endpoint = entityType === 'territories' ? '/ce-territories' : `/${entityType}`;
      return await this.request<T[]>(`${endpoint}/batch`, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      });
    } catch (error) {
      throw new Error(`Failed to batch get ${entityType}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Batch update entities
   */
  batchUpdateEntities = async <T>(
    entityType: 'territories' | 'circles' | 'districts' | 'divisions' | 'sub-divisions' | 'mandals',
    updates: Array<{ id: number; data: Partial<T> }>
  ): Promise<BulkOperationResultDto> => {
    try {
      const endpoint = entityType === 'territories' ? '/ce-territories' : `/${entityType}`;
      const result = await this.request<BulkOperationResultDto>(`${endpoint}/batch-update`, {
        method: 'PUT',
        body: JSON.stringify({ updates }),
      }, false);
      
      this.invalidateCache(endpoint);
      return result;
    } catch (error) {
      throw new Error(`Failed to batch update ${entityType}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Test API connectivity
   */
  testConnection = async (): Promise<{ status: 'connected' | 'error'; message: string; timestamp: string }> => {
    try {
      const response = await this.request<{ status: string; timestamp: string }>('/health/check');
      return {
        status: 'connected',
        message: 'API connection successful',
        timestamp: response.timestamp
      };
    } catch (error) {
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Connection failed',
        timestamp: new Date().toISOString()
      };
    }
  };

  /**
   * Get API version and info
   */
  getApiInfo = async (): Promise<{
    version: string;
    environment: string;
    uptime: number;
    features: string[];
  }> => {
    try {
      return await this.request<{
        version: string;
        environment: string;
        uptime: number;
        features: string[];
      }>('/info');
    } catch (error) {
      throw new Error(`Failed to get API info: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Download file by URL
   */
  downloadFile = async (url: string, filename: string): Promise<void> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw new Error(`Failed to download file: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Upload file
   */
  uploadFile = async (
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>
  ): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value.toString());
        });
      }

      return await this.request<any>(endpoint, {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set Content-Type for multipart/form-data
      }, false);
    } catch (error) {
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // ADVANCED SEARCH & FILTERING
  // ==========================================

  /**
   * Advanced search with complex filters
   */
  advancedSearch = async (searchCriteria: {
    term?: string;
    entityTypes?: string[];
    filters?: Record<string, any>;
    dateRange?: { from: Date; to: Date };
    coordinates?: { lat: number; lng: number; radius: number };
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    page?: number;
    limit?: number;
  }): Promise<SearchResultsDto> => {
    try {
      return await this.request<SearchResultsDto>('/territorial/advanced-search', {
        method: 'POST',
        body: JSON.stringify(searchCriteria),
      });
    } catch (error) {
      throw new Error(`Failed to perform advanced search: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get territorial hierarchy for a mandal
   */
  getFullHierarchy = async (mandalId: number): Promise<{
    mandal: Mandal;
    subDivision: SubDivision;
    division: Division;
    district: District;
    circle: Circle;
    territory: CETerritory;
  }> => {
    try {
      return await this.request<{
        mandal: Mandal;
        subDivision: SubDivision;
        division: Division;
        district: District;
        circle: Circle;
        territory: CETerritory;
      }>(`/mandals/${mandalId}/full-hierarchy`);
    } catch (error) {
      throw new Error(`Failed to get full hierarchy for mandal ${mandalId}: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  /**
   * Get entities within geographic bounds
   */
  getEntitiesInBounds = async (
    entityType: 'districts' | 'mandals',
    bounds: {
      northEast: { lat: number; lng: number };
      southWest: { lat: number; lng: number };
    }
  ): Promise<any[]> => {
    try {
      return await this.request<any[]>(`/${entityType}/in-bounds`, {
        method: 'POST',
        body: JSON.stringify(bounds),
      });
    } catch (error) {
      throw new Error(`Failed to get ${entityType} in bounds: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // ==========================================
  // ERROR HANDLING HELPERS
  // ==========================================

  /**
   * Handle API errors with specific error types
   */
  private handleApiError = (error: any): never => {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - please check your connection');
    }
    
    if (error.status) {
      switch (error.status) {
        case 400:
          throw new Error('Invalid request - please check your input');
        case 401:
          throw new Error('Authentication required - please log in');
        case 403:
          throw new Error('Access denied - insufficient permissions');
        case 404:
          throw new Error('Resource not found');
        case 429:
          throw new Error('Too many requests - please wait and try again');
        case 500:
          throw new Error('Server error - please try again later');
        case 502:
        case 503:
        case 504:
          throw new Error('Service temporarily unavailable - please try again');
        default:
          throw new Error(`API error: ${error.status} ${error.statusText}`);
      }
    }
    
    throw error;
  };
}

// ==========================================
// SINGLETON INSTANCE AND EXPORT
// ==========================================

export const territorialAPI = new TerritorialAPIService();

// Export the class for custom instances
export { TerritorialAPIService };

// Export type definitions for consumers
export type {
  CETerritory,
  Circle,
  District,
  Division,
  SubDivision,
  Mandal,
  QueryOptionsDto,
  PaginationResponseDto,
  SearchResultsDto,
  TerritorialStatsDto,
  HierarchyResponseDto,
  BulkOperationResultDto
};

